/**
 * KAN-810: dialogs of the bulk actions: "Отметить переданными" (required
 * "Кому передан") and "Назначить менеджера" (confirm only after an explicit
 * choice; "Без менеджера" sends null).
 */
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ManagerSelect } from '@/components/managers/ManagerSelect';
import { useAssignableManagers } from '@/hooks/useManagers';

interface BaseDialogProps {
  open: boolean;
  count: number;
  pending: boolean;
  onClose: () => void;
}

export function LeadTransferDialog({
  open,
  count,
  pending,
  onClose,
  onConfirm,
}: BaseDialogProps & { onConfirm: (transferredTo: string) => void }) {
  const [value, setValue] = useState('');
  const inputId = useId();
  useEffect(() => {
    if (open) setValue('');
  }, [open]);
  const trimmed = value.trim();
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Отметить переданными</DialogTitle>
          <DialogDescription>Выбрано лидов: {count}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={inputId}>Кому передан</Label>
          <Input
            id={inputId}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Например, отдел продаж"
            maxLength={200}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            data-testid="lead-transfer-confirm"
            disabled={!trimmed || pending}
            onClick={() => onConfirm(trimmed)}
          >
            Отметить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LeadAssignManagerDialog({
  open,
  count,
  pending,
  onClose,
  onConfirm,
}: BaseDialogProps & { onConfirm: (managerId: string | null) => void }) {
  // undefined: nothing chosen yet; null: "Без менеджера" chosen explicitly.
  const [managerId, setManagerId] = useState<string | null | undefined>(undefined);
  const { data: managers } = useAssignableManagers();
  useEffect(() => {
    if (open) setManagerId(undefined);
  }, [open]);
  const chosen = managers.find((m) => m.id === managerId) ?? null;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Назначить менеджера</DialogTitle>
          <DialogDescription>Выбрано лидов: {count}</DialogDescription>
        </DialogHeader>
        <ManagerSelect
          value={chosen}
          onSelect={setManagerId}
          testId="lead-bulk-manager-select"
          noneLabel="Без менеджера"
          notifyUnchanged
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            data-testid="lead-bulk-assign-confirm"
            disabled={pending || managerId === undefined}
            onClick={() => {
              if (managerId !== undefined) onConfirm(managerId);
            }}
          >
            Назначить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
